import { Col, Row } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import InputFiles from "components/panels/Run/InputFiles";
import Parameters from "components/panels/Run/Parameters";
import JobInfoPanel from "components/panels/Run/InfoPanel";
import ButtonVelocity from "components/assets/utilities/ButtonVelocity";
import { useAppDispatch, useAppSelector, useIsReadOnly } from "redux/hooks";
import { useEffect, useState } from "react";
import { modifyConfig, setConfig, setJobPanelsComplete } from "redux/slices/user";
import { LoadFilesAction } from "redux/actions/LoadFiles";
import { RunModelAction } from "redux/actions/RunModel";
import { RefreshAction } from "redux/actions/Refresh";
import Field from "redux/classes/Field";
import { endSubmitting, requestCompleted, requestFailure, startSubmitting } from "redux/slices/app";
import { useSession } from "components/wrappers/SessionProvider";
import Constraints from "components/panels/Run/Constraints";
import Segments from "components/panels/Run/Segments";
import Tactics from "components/panels/Run/Tactics";
import { RunPageWrapper } from "./styles";

const RunPage = () => {
  const dispatch = useAppDispatch();
  const app = useAppSelector(state => state.app);
  const user = useAppSelector(state => state.user);
  const modelInfo = user.models[user.modelIndex];
  const pageLocation = useLocation().pathname;
  const navigate = useNavigate();
  const session = useSession();
  const isReadOnly = useIsReadOnly();
  // Bumped by the Reset button to force the input/parameter panels to remount
  // so their internal antd Form state (Select values, Slider positions, etc.)
  // gets rebuilt — `setConfig({reset:true})` clears the redux config, but the
  // antd Forms own their displayed values and won't update unless remounted.
  const [resetTick, setResetTick] = useState(0);

  useEffect(() => {
    dispatch(setConfig({ reset: true }))

    const getFiles = async () => {
      await dispatch(LoadFilesAction(session, modelInfo.alias, modelInfo.inputs))
      dispatch(requestCompleted());
    }
    getFiles().catch(() => dispatch(requestFailure()))

    const outputs: {[key: string]: string} = {}
    modelInfo.outputs.map(output => {
      outputs[output.fieldType] = output.label + output.dataType
      return true
    })

    dispatch(modifyConfig({
      type: "outputs",
      value: outputs
    }))
  }, [])

  useEffect(() => {
    if (user.jobPanelsComplete) {
      if (Object.values(user.jobPanelsComplete!).every(value => value)) {
        const runModel = async () => {
          await dispatch(RunModelAction(session, user.config, modelInfo)).catch(console.error);
          await dispatch(RefreshAction(session)).catch(console.error);
          dispatch(setJobPanelsComplete({ jobPanelsComplete: false }))
          navigate(`/${modelInfo.alias}/info`)
        }
        runModel().catch(() => dispatch(endSubmitting()))
      }
    }
    else {
      dispatch(endSubmitting());
    }

  }, [user.jobPanelsComplete])

  return (
    <RunPageWrapper>
      {app.loading ? <div className="loader-centered"/> :
        <>
          <Row gutter={20}>
            <Col md={24} className="input-container">
              <JobInfoPanel key={"info-" + resetTick}/>
            </Col>
          </Row>

          {modelInfo.inputs.length > 0 &&
            <Row>
              <Col md={24}>
                <InputFiles key={"inputs-" + resetTick}/>
              </Col>
            </Row>
          }

          {modelInfo.parameters.length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                <Parameters key={"params-" + resetTick}/>
              </Col>
            </Row>
          }

          {modelInfo.parameters.filter((field: Field) => field.fieldType === "sliders").length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                <Constraints key={"constraints-" + resetTick}/>
              </Col>
            </Row>
          }

          {modelInfo.parameters.filter((field: Field) => field.fieldType === "segment").length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                <Segments key={"segments-" + resetTick}/>
              </Col>
            </Row>
          }

          {modelInfo.parameters.filter((field: Field) => field.fieldType === "tactics").length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                <Tactics key={"tactics-" + resetTick}/>
              </Col>
            </Row>
          }

          {(() => {
            // Honest progress text based on actual config completeness — counts
            // both required inputs and required parameters, and rejects values
            // that fail their type validation (NaN from typing letters into an
            // int/float field, empty array from clearing a multi-select, etc.)
            // so a field with a red validation error doesn't count as filled.
            const isValidValue = (value: any): boolean => {
              if (value === undefined || value === null) return false;
              if (typeof value === "string" && value === "") return false;
              if (typeof value === "number" && Number.isNaN(value)) return false;
              if (Array.isArray(value)) {
                if (value.length === 0) return false;
                if (value.some((x: any) => typeof x === "number" && Number.isNaN(x))) return false;
              }
              return true;
            };
            const requiredInputs = modelInfo.inputs.filter((i: any) => !i.optional);
            const requiredParameters = modelInfo.parameters.filter((p: any) => !p.optional);
            const filledInputs = requiredInputs.filter((i: any) => isValidValue(user.config?.inputs?.[i.label])).length;
            const filledParameters = requiredParameters.filter((p: any) => isValidValue(user.config?.parameters?.[p.label])).length;
            const totalRequired = requiredInputs.length + requiredParameters.length;
            const filled = filledInputs + filledParameters;
            const progressText = totalRequired === 0
              ? "No required fields"
              : `${filled} of ${totalRequired} required field${totalRequired === 1 ? "" : "s"} configured`;

            return (
              <div className="run-footer">
                <div className="run-footer-left">
                  <ButtonVelocity
                    type="secondary"
                    title={isReadOnly ? "Read-only — exit Admin View to reset" : "Clear the job alias, description, all selected input files, and all parameter values"}
                    path={null}
                    disable={isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      dispatch(setConfig({ reset: true }));
                      setResetTick(t => t + 1);
                    }}
                  >
                    Reset
                  </ButtonVelocity>
                </div>
                <div className="run-footer-status">{isReadOnly ? "Admin View — read-only" : progressText}</div>
                <div className="run-footer-right">
                  <ButtonVelocity
                    type="primary"
                    title={isReadOnly ? "Read-only — exit Admin View to run" : "Run a " + modelInfo.name + " job with the specified configuration"}
                    path={pageLocation}
                    disable={isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      let jobPanelsComplete: {[key: string]: boolean} = {
                        'meta': false
                      }
                      if (modelInfo.inputs.length > 0) {
                        jobPanelsComplete['inputs'] = false
                      }
                      if (modelInfo.parameters.length > 0) {
                        jobPanelsComplete['parameters'] = false
                      }
                      dispatch(setJobPanelsComplete({ jobPanelsComplete: jobPanelsComplete }))

                      dispatch(startSubmitting())
                    }}
                    submitting={app.submitting}
                  >
                    Run
                  </ButtonVelocity>
                </div>
              </div>
            );
          })()}
        </>
      }
    </RunPageWrapper>
  );
};

export default RunPage;